import React, { useState } from 'react'
import "./Modulemoderators.scss"
import { useQuery, useMutation } from 'react-query';
import axios from 'axios';
import { Baseurl } from './../../../Api/Baseurl';
import { MdDelete } from "react-icons/md";
import { MdSearch } from "react-icons/md";
import { MdDragIndicator } from "react-icons/md";
import { MdAdd } from "react-icons/md";
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { ToastOptions } from '../../../helpers/ToastOptions';
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

export default function AssignedModules() {
    const { moderatorId } = useParams();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [notAssignedSearchTerm, setNotAssignedSearchTerm] = useState('');
    const [assignedSearchInput, setAssignedSearchInput] = useState('');
    const [notAssignedSearchInput, setNotAssignedSearchInput] = useState('');
    const token = localStorage.getItem('medtoken');

    // Fetch assigned modules for the moderator
    const fetchAssignedModules = async () => {
        const response = await axios.get(`${Baseurl}/admin/moduleModerator/${moderatorId}/assignedModules`, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response.data.payload;
    };

    // Fetch not assigned modules (without search params)
    const fetchNotAssignedModules = async () => {
        const response = await axios.get(`${Baseurl}/modules/notassigned`, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response.data.payload;
    };

    // React Query hooks
    const { data: assignedData, isLoading: assignedLoading, refetch: refetchAssigned } = useQuery(
        ['assignedModules', moderatorId],
        fetchAssignedModules,
        {
            enabled: !!moderatorId,
            onError: (error) => {
                const errorMessage = error.response?.data?.msg || "Failed to fetch assigned modules";
                toast.error(errorMessage, ToastOptions("error"));
            }
        }
    );

    const { data: notAssignedData, isLoading: notAssignedLoading, refetch: refetchNotAssigned } = useQuery(
        ['notAssignedModules'],
        fetchNotAssignedModules,
        {
            onError: (error) => {
                const errorMessage = error.response?.data?.msg || "Failed to fetch not assigned modules";
                toast.error(errorMessage, ToastOptions("error"));
            }
        }
    );

    // Assign module mutation
    const assignModuleMutation = useMutation(
        async ({ moduleId, moduleModeratorId }) => {
            const response = await axios.post(`${Baseurl}/admin/assignModuleModerator`, {
                moduleId,
                moduleModeratorId
            }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            return response.data;
        },
        {
            onSuccess: () => {
                toast.success("Module assigned successfully", ToastOptions("success"));
                refetchAssigned();
                refetchNotAssigned();
            },
            onError: (error) => {
                const errorMessage = error.response?.data?.msg || "Failed to assign module";
                toast.error(errorMessage, ToastOptions("error"));
            }
        }
    );

    // Unassign module mutation
    const unassignModuleMutation = useMutation(
        async (moduleId) => {
            const response = await axios.delete(`${Baseurl}/admin/unassignModuleModerator/${moduleId}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            return response.data;
        },
        {
            onSuccess: () => {
                toast.success("Module unassigned successfully", ToastOptions("success"));
                refetchAssigned();
                refetchNotAssigned();
            },
            onError: (error) => {
                const errorMessage = error.response?.data?.msg || "Failed to unassign module";
                toast.error(errorMessage, ToastOptions("error"));
            }
        }
    );

    // Handle drag and drop
    const handleDragStart = (e, module) => {
        console.log('Drag started with module:', module.title);
        console.log('Module data:', module);
        
        // Try multiple data transfer methods
        try {
            e.dataTransfer.setData('application/json', JSON.stringify(module));
            e.dataTransfer.setData('text/plain', JSON.stringify(module));
            e.dataTransfer.setData('text/html', JSON.stringify(module));
        } catch (error) {
            console.error('Error setting drag data:', error);
        }
        
        e.dataTransfer.effectAllowed = 'move';
        // Add visual feedback
        e.target.style.opacity = '0.5';
    };

    const handleDragEnd = (e) => {
        e.target.style.opacity = '1';
    };

    const handleDragOver = (e) => {
        console.log('Drag over detected');
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        // Add visual feedback to drop zone
        e.currentTarget.classList.add('drag-over');
    };

    const handleDragLeave = (e) => {
        e.currentTarget.classList.remove('drag-over');
    };

    const handleDrop = (e) => {
        console.log('Drop event triggered');
        e.preventDefault();
        e.currentTarget.classList.remove('drag-over');
        
        // Try to get data from multiple sources
        let moduleData = null;
        let jsonData = '';
        
        // Try different data transfer types
        try {
            jsonData = e.dataTransfer.getData('application/json');
            console.log('JSON data from application/json:', jsonData);
        } catch (error) {
            console.log('Failed to get application/json data');
        }
        
        if (!jsonData || jsonData.trim() === '') {
            try {
                jsonData = e.dataTransfer.getData('text/plain');
                console.log('JSON data from text/plain:', jsonData);
            } catch (error) {
                console.log('Failed to get text/plain data');
            }
        }
        
        if (!jsonData || jsonData.trim() === '') {
            try {
                jsonData = e.dataTransfer.getData('text/html');
                console.log('JSON data from text/html:', jsonData);
            } catch (error) {
                console.log('Failed to get text/html data');
            }
        }
        
        console.log('Final JSON data to parse:', jsonData);
        
        if (!jsonData || jsonData.trim() === '') {
            console.error('No drag data found in any format');
            return;
        }
        
        try {
            moduleData = JSON.parse(jsonData);
            console.log('Successfully parsed module data:', moduleData);
            
            Swal.fire({
                title: "Assign Module",
                text: `Are you sure you want to assign "${moduleData.title}" to this moderator?`,
                icon: "question",
                showCancelButton: true,
                confirmButtonColor: "#01677e",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, assign it!"
            }).then((result) => {
                if (result.isConfirmed) {
                    assignModuleMutation.mutate({
                        moduleId: moduleData.id,
                        moduleModeratorId: parseInt(moderatorId)
                    });
                }
            });
        } catch (error) {
            console.error('Error parsing drag data:', error);
            console.error('Raw data that failed to parse:', jsonData);
        }
    };

    // Handle unassign
    const handleUnassign = (moduleId, moduleTitle) => {
        Swal.fire({
            title: "Unassign Module",
            text: `Are you sure you want to unassign "${moduleTitle}" from this moderator?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#01677e",
            confirmButtonText: "Yes, unassign it!"
        }).then((result) => {
            if (result.isConfirmed) {
                unassignModuleMutation.mutate(moduleId);
            }
        });
    };

    // Handle click to assign (fallback for devices that don't support drag and drop well)
    const handleClickToAssign = (module) => {
        Swal.fire({
            title: "Assign Module",
            text: `Are you sure you want to assign "${module.title}" to this moderator?`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#01677e",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, assign it!"
        }).then((result) => {
            if (result.isConfirmed) {
                assignModuleMutation.mutate({
                    moduleId: module.id,
                    moduleModeratorId: parseInt(moderatorId)
                });
            }
        });
    };

    // Handle search for assigned modules
    const handleAssignedSearch = () => {
        setSearchTerm(assignedSearchInput);
    };

    // Handle search for not assigned modules
    const handleNotAssignedSearch = () => {
        setNotAssignedSearchTerm(notAssignedSearchInput);
    };

    // Handle Enter key press
    const handleKeyPress = (e, searchType) => {
        if (e.key === 'Enter') {
            if (searchType === 'assigned') {
                handleAssignedSearch();
            } else if (searchType === 'notAssigned') {
                handleNotAssignedSearch();
            }
        }
    };

    // Client-side filtering functions
    const getFilteredAssignedModules = () => {
        if (!assignedData?.assignedModules) return [];
        
        if (!searchTerm) return assignedData.assignedModules;
        
        return assignedData.assignedModules.filter(module => 
            module.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            module.grade.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (module.description && module.description.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    };

    const getFilteredNotAssignedModules = () => {
        if (!notAssignedData?.modules) return [];
        
        if (!notAssignedSearchTerm) return notAssignedData.modules;
        
        return notAssignedData.modules.filter(module => 
            module.title.toLowerCase().includes(notAssignedSearchTerm.toLowerCase()) ||
            module.grade.toLowerCase().includes(notAssignedSearchTerm.toLowerCase()) ||
            (module.description && module.description.toLowerCase().includes(notAssignedSearchTerm.toLowerCase()))
        );
    };

    // Animation variants
    const animationVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -20 }
    };

    if (assignedLoading || notAssignedLoading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading modules...</p>
            </div>
        );
    }

    return (
        <div className="assigned-modules-container">
            {/* Back button at the top */}
            <div className="back-button-container top">
                <button 
                    className="back-button"
                    onClick={() => navigate('/modulemoderatores')}
                >
                    ← Back to Module Moderators
                </button>
            </div>

            {/* Header */}
            <div className="page-header">
                <h1 className="page-title">Module Assignment</h1>
                {assignedData?.moduleModerator && (
                    <div className="moderator-info">
                        <h2 className="moderator-name">{assignedData.moduleModerator.name}</h2>
                        <p className="moderator-details">
                            {assignedData.moduleModerator.email} • {assignedData.moduleModerator.university}
                        </p>
                    </div>
                )}
            </div>

            {/* Two Column Layout */}
            <div className="modules-layout">
                {/* Assigned Modules Column */}
                <motion.div 
                    className="assigned-modules-column"
                    variants={animationVariants}
                    initial="hidden"
                    animate="visible"
                    transition={{ duration: 0.5 }}
                >
                    <div className="column-header">
                        <h3 className="column-title">Assigned Modules</h3>
                        <span className="module-count">
                            {assignedData?.totalModules || 0} modules
                        </span>
                    </div>

                    {/* Search for assigned modules */}
                    <div className="search-container">
                        <div className="search-input-wrapper">
                            <input
                                type="text"
                                placeholder="Search assigned modules..."
                                value={assignedSearchInput}
                                onChange={(e) => setAssignedSearchInput(e.target.value)}
                                onKeyPress={(e) => handleKeyPress(e, 'assigned')}
                                className="search-input"
                            />
                            <MdSearch 
                                className="search-icon" 
                                onClick={handleAssignedSearch}
                                style={{ cursor: 'pointer' }}
                            />
                        </div>
                    </div>

                    {/* Assigned modules list */}
                    <div 
                        className="modules-list assigned-list"
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        {getFilteredAssignedModules().map((module) => (
                            <motion.div
                                key={module.id}
                                className="module-card assigned-module"
                                variants={animationVariants}
                                initial="hidden"
                                animate="visible"
                                transition={{ duration: 0.3 }}
                            >
                                <div className="module-content">
                                    <h4 className="module-title">{module.title}</h4>
                                    <p className="module-grade">Grade: {module.grade}</p>
                                    {module.description && (
                                        <p className="module-description">{module.description}</p>
                                    )}
                                    <div className="module-status">
                                        <span className={`status-badge ${module.status ? 'active' : 'inactive'}`}>
                                            {module.status ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                </div>
                                <div className="module-actions">
                                    <button
                                        className="unassign-btn"
                                        onClick={() => handleUnassign(module.id, module.title)}
                                        title="Unassign module"
                                    >
                                        <MdDelete />
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                        
                        {/* Always show drop zone - either as empty state or after assigned modules */}
                        <div 
                            className={`drop-zone ${assignedData?.assignedModules?.length === 0 ? 'empty-state' : 'persistent'}`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => {
                                // Scroll to available modules and highlight them
                                const availableModules = document.querySelector('.not-assigned-modules-column');
                                if (availableModules) {
                                    availableModules.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    availableModules.style.animation = 'pulse 2s ease-in-out';
                                    setTimeout(() => {
                                        availableModules.style.animation = '';
                                    }, 2000);
                                }
                            }}
                        >
                            <div className="drop-zone-content">
                                <div className="drop-zone-icon">
                                    <MdDragIndicator />
                                </div>
                                <h4>Drag any module here to assign</h4>
                                <p>Drop modules from the available list to assign them to this moderator</p>
                                <div className="drop-zone-hint">
                                    <span>Click here to view available modules</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Not Assigned Modules Column */}
                <motion.div 
                    className="not-assigned-modules-column"
                    variants={animationVariants}
                    initial="hidden"
                    animate="visible"
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    <div className="column-header">
                        <h3 className="column-title">Available Modules</h3>
                        <span className="module-count">
                            {notAssignedData?.modules?.length || 0} modules
                        </span>
                    </div>

                    {/* Search for not assigned modules */}
                    <div className="search-container">
                        <div className="search-input-wrapper">
                            <input
                                type="text"
                                placeholder="Search available modules..."
                                value={notAssignedSearchInput}
                                onChange={(e) => setNotAssignedSearchInput(e.target.value)}
                                onKeyPress={(e) => handleKeyPress(e, 'notAssigned')}
                                className="search-input"
                            />
                            <MdSearch 
                                className="search-icon" 
                                onClick={handleNotAssignedSearch}
                                style={{ cursor: 'pointer' }}
                            />
                        </div>
                    </div>

                    {/* Not assigned modules list */}
                    <div 
                        className="modules-list not-assigned-list"
                    >
                        {getFilteredNotAssignedModules().map((module) => (
                            <motion.div
                                key={module.id}
                                className="module-card not-assigned-module"
                                draggable
                                onDragStart={(e) => handleDragStart(e, module)}
                                onDragEnd={handleDragEnd}
                                onClick={() => handleClickToAssign(module)}
                                variants={animationVariants}
                                initial="hidden"
                                animate="visible"
                                transition={{ duration: 0.3 }}
                            >
                                <div className="drag-indicator">
                                    <MdDragIndicator />
                                </div>
                                <div className="module-content">
                                    <h4 className="module-title">{module.title}</h4>
                                    <p className="module-grade">Grade: {module.grade}</p>
                                    {module.description && (
                                        <p className="module-description">{module.description}</p>
                                    )}
                                    <div className="module-stats">
                                        <span className="stat-item">
                                            QBanks: {module.qbanksCount || 0}
                                        </span>
                                        <span className="stat-item">
                                            Files: {module.filesCount || 0}
                                        </span>
                                        <span className="stat-item">
                                            Flashcards: {module.flashcardsCount || 0}
                                        </span>
                                    </div>
                                </div>
                                <div className="assign-hint">
                                    <span className="desktop-text">Click or drag to assign</span>
                                    <span className="mobile-text">
                                        <MdAdd className="plus-icon" />
                                    </span>
                                </div>
                            </motion.div>
                        ))}
                        
                        {getFilteredNotAssignedModules().length === 0 && (
                            <div className="empty-state">
                                <p>{notAssignedSearchTerm ? 'No modules match your search' : 'No available modules to assign'}</p>
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
